<template>
    <div id="lm-form-builder-ui" class="wrapper-content w-100">
      <h1>{{$t('form_builder')}}</h1>
      <h3>{{$t('general_settings')}}</h3>
      <div class="row position-relative mt-4">
        <LmComponentLoading v-if="isLoading" fixed />
        <div class="col-12">
          <LmForm ref="lmFormRef" id="form-builder-ui-form">
            <LmTextInput
              class="col-12"
              v-model="form.pageTitleData"
              :v="$v.form.pageTitleData"
              :label="$t('form_title')" />
            <LmTextInput
              class="col-12 col-md-4"
              id="input-alias"
              v-model="form.alias"
              :v="$v.form.alias"
              :label="$t('form_alias')" />
            <LmTextInput
              class="col-12 col-md-4"
              v-model="form.apiRestForHandleAllHttpMethods"
              :v="$v.form.apiRestForHandleAllHttpMethods"
              :label="$t('endpoint_to_manage_all_requests')"
              :isApiPath="true"
              :help="$t('endpoint_to_manage_all_requests_help')"
            />
            <LmTextInput
              class="col-12 col-md-4"
              v-model="form.pathToRedirectAfterPOSTorPUT"
              :label="$t('path_to_redirect')" />
            <LmTextInput
              class="col-12"
              v-model="form.messageAfterPOSTorPUT"
              :label="$t('success_action_message_setter')" />
            <LmCheckbox
              class="col-12"
              :labelSingle="$t('is_internal_form')"
              v-model="form.lumbreInternal"
              v-if="userInfo.role === 'Superusuario'"
            />
            <hr class="w-100">
            <div class="col-12 mt-3">
              <b-form-group :label="$t('view_modes')">
                <b-form-radio-group buttons button-variant="primary" v-model="modes.selected">
                  <b-form-radio
                    name="viewMode"
                    value="edit"
                    @change="modes.editView = $event === 'edit'"
                  >
                    <font-awesome-icon class="mr-1" icon="wrench" />
                    {{ $t('edit_mode') }}
                  </b-form-radio>
                  <b-form-radio
                    name="viewMode"
                    value="preview"
                    @change="modes.editView = $event === 'edit'"
                  >
                    <font-awesome-icon class="mr-1" icon="eye" />
                    {{ $t('preview_mode') }}
                  </b-form-radio>
                </b-form-radio-group>
              </b-form-group>
            </div>
            <div class="col-12" v-if="componentsOptions.content.length">
              <h3>{{ $t('form_fields') }}</h3>
              <div class="row">
                <div class="col-12 col-md-6" v-show="modes.editView">
                  <draggable
                    v-model="form.formElements"
                    @start="drag=true"
                    @end="drag=false"
                    animation="150"
                    handle=".handle"
                    >
                    <div v-for="(formElement, index) in form.formElements" :key="formElement.id" class="mt-4">
                      <header
                        class="d-flex justify-content-between align-items-center shadow rounded-lg text-white bg-primary px-3 handle"
                        :class="{'bg-info': !shouldCreatePortal(form.formElements[index].componentName)}"
                      >
                        <font-awesome-icon class="mr-4" icon="arrows-alt" style="width:18px; height:18px;" />
                        <h4 v-b-toggle="`collapse-${index}`" class="flex-grow-1 text-center my-0 py-3 handle">{{title(form.formElements[index])}}</h4>
                        <div
                          v-if="shouldCreatePortal(form.formElements[index].componentName)"
                          class="circle d-flex justify-content-center align-items-center"
                        >
                          {{index}}
                        </div>
                        <div class="d-flex align-items-center">
                          <font-awesome-icon v-b-toggle="`collapse-${index}`" class="ml-4 clickable" icon="chevron-down" style="width:16px; height:16px;" />
                          <font-awesome-icon v-if="form.formElements.length > 1" class="ml-4 clickable" icon="trash" style="width:16px; height:16px;" @click="removeFormElement(index)" />
                        </div>
                      </header>
                      <b-collapse
                        accordion="my-accordion"
                        :id="`collapse-${index}`"
                        visible
                        class="p-4 border border-top-0 rounded-lg"
                        >
                        <LmSelect
                          :id="'select-elements-' + index"
                          v-model="form.formElements[index].componentName"
                          :options="componentsOptions"
                          label="Seleccione el tipo de elemento"
                          @input="selectorElementForm($event, index)"
                        />
                        <component
                          :is="whatComponentBuilderUse(form.formElements[index].componentName)"
                          :id="form.formElements[index].formObjectToSendToServerProperty || form.formElements[index].id"
                          :index="index"
                          :componentData="form.formElements[index]"
                          :optionsChildOf="buildOptionsChildOf(form.formElements[index].formObjectToSendToServerProperty)"
                          :leftValueToCompareOptions="leftValueToCompareOptions(form.formElements[index])"
                          :visibilityDependOnConditions="form.formElements[index].visibilityDependOnConditions"
                          :optionsSameAsValidation="{content: buildSameAsOptions(form.formElements, index)}"
                          :validationsData="getValidation(form.formElements[index].formObjectToSendToServerProperty)"
                          :options="lmSuperPowerSelectV2BuilderUiOptions"
                          :isOnEditMode="isOnEditMode"
                          @input="inputHandler($event, index)"
                          @validations-changes="validationsChangesHandler"
                        />
                      </b-collapse>
                    </div>
                  </draggable>
                </div>
                <div class="col-12 mt-4" :class="{ 'col-md-6': modes.editView }">
                  <LmFormBuilder
                    :getComponents="form"
                    :isOnPreviewMode="modes.editView"
                    @openEditFormElement="openEditFormElement"
                  />
                </div>
              </div>
            </div>
            <p v-if="$v.$error && !$v.form.formElements.required" class="invalid-feedback d-block">
              {{ $t('form_must_have_at_least_one_field') }}
            </p>
            <div class="col-12">
              <LmButton
                v-if="modes.editView"
                id="add-field-button"
                class="mt-4"
                type="button"
                lmButtonRootWrapperClass="d-inline-block"
                lmButtonWrapperClass="col"
                @click.native="addFormElement(form.formElements.length)"
                :value="$t('add_field_to_form')"
              />
            </div>
          </LmForm>
        </div>
        <div class="col-12 mt-4" v-show="modes.editView">
          <LmButton
            id="submit-button"
            type="button"
            :value="$t('save')"
            buttonClass="btn-primary"
            lmButtonRootWrapperClass="d-inline-block"
            @click.native="submit"/>
        </div>
      </div>
    </div>
</template>

<script>
import LmForm from '@/components/form/LmForm'
import LmTextInput from '@/components/form/elements/LmTextInput'
import LmButton from '@/components/form/elements/LmButton'
import LmSelect from '@/components/form/elements/LmSelect'
import LmSuperPowerSelectV2 from '@/components/form/elements/LmSuperPowerSelectV2'
import LmCheckbox from '@/components/form/elements/LmCheckbox'
import draggable from 'vuedraggable'
import LmFormBuilder from '@/components/LmFormBuilder'

import LmTextNumberEditorTextAreaPasswordBuilderUi from '@/components/lmFormBuilderUi/LmTextNumberEditorTextAreaPasswordBuilderUi'
import LmEmailInputBuilderUi from '@/components/lmFormBuilderUi/LmEmailInputBuilderUi'
import LmFormSubtitlesBuilderUi from '@/components/lmFormBuilderUi/LmFormSubtitlesBuilderUi'
import LmFormEmptySpaceBuilderUi from '@/components/lmFormBuilderUi/LmFormEmptySpaceBuilderUi'
import LmFormSeparatorBuilderUi from '@/components/lmFormBuilderUi/LmFormSeparatorBuilderUi'
import LmSelectBuilderUi from '@/components/lmFormBuilderUi/LmSelectBuilderUi'
import LmRadioBuilderUi from '@/components/lmFormBuilderUi/LmRadioBuilderUi'
import LmSuperPowerSelectV2BuilderUi from '@/components/lmFormBuilderUi/LmSuperPowerSelectV2BuilderUi'
import LmButtonBuilderUi from '@/components/lmFormBuilderUi/LmButtonBuilderUi'
import LmDateBuilderUi from '@/components/lmFormBuilderUi/LmDateBuilderUi'
import LmDateTimeBuilderUi from '@/components/lmFormBuilderUi/LmDateTimeBuilderUi'
import LmLeaFletMapBuilderUi from '@/components/lmFormBuilderUi/LmLeaFletMapBuilderUi'
import LmHtmlContentBuilderUi from '@/components/lmFormBuilderUi/LmHtmlContentBuilderUi'
import LmFormPortalBuilderUi from '@/components/lmFormBuilderUi/LmFormPortalBuilderUi'
import LmColorPickerBuilderUi from '@/components/lmFormBuilderUi/LmColorPickerBuilderUi'
import LmCommentBuilderUi from '@/components/lmFormBuilderUi/LmCommentBuilderUi'
import LmRepeatFieldsBuilderUi from '@/components/lmFormBuilderUi/LmRepeatFieldsBuilderUi'
import LmCheckboxBuilderUi from '@/components/lmFormBuilderUi/LmCheckboxBuilderUi'
import LmUploadFilesBuilderUi from '@/components/lmFormBuilderUi/LmUploadFilesBuilderUi'
import LmUploadImageBuilderUi from '@/components/lmFormBuilderUi/LmUploadImageBuilderUi'
import LmImportFromExcelBuilderUi from '@/components/lmFormBuilderUi/LmImportFromExcelBuilderUi'
import LmCustomBuilderUi from '@/components/lmFormBuilderUi/LmCustomBuilderUi'
import LmSignersBuilderUi from '@/components/lmFormBuilderUi/LmSignersBuilderUi'
import LmCodeInputBuilderUi from '@/components/lmFormBuilderUi/LmCodeInputBuilderUi'
import LmTreeSelectBuilderUi from '@/components/lmFormBuilderUi/LmTreeSelectBuilderUi'
import LmEditorBuilderUi from '@/components/lmFormBuilderUi/LmEditorBuilderUi'
import LmTagsInputBuilderUi from '@/components/lmFormBuilderUi/LmTagsInputBuilderUi'
import LmPageBreakBuilderUi from '@/components/lmFormBuilderUi/LmPageBreakBuilderUi'
import LmDatePickerBuilderUi from '@/components/lmFormBuilderUi/LmDatePickerBuilderUi'

import commonsApi from '@/api/commons'
import { required } from 'vuelidate/lib/validators'
import lmToasts from '@/mixins/lmToasts.js'
import lmFormBuilderUi from '@/mixins/lmFormBuilderUi.js'

export default {
  mixins: [lmToasts, lmFormBuilderUi],
  provide () {
    const context = {}
    for (const key in this.form) {
      Object.defineProperty(context, key, {
        enumerable: true,
        get: () => this.form[key]
      })
    }
    return { context }
  },
  data () {
    return {
      isLoading: false,
      currentID: this.$route?.params?.id || '',
      modes: {
        selected: 'edit',
        editView: true
      },
      form: {
        alias: '',
        pageTitleData: '',
        messageAfterPOSTorPUT: '',
        pathToRedirectAfterPOSTorPUT: '',
        apiRestForHandleAllHttpMethods: '/',
        validations: {},
        formElements: [],
        lumbreInternal: false
      },
      initialForm: {}
    }
  },
  validations: {
    form: {
      pageTitleData: {
        required
      },
      alias: {
        required
      },
      apiRestForHandleAllHttpMethods: {
        required
      },
      formElements: {
        required
      }
    }
  },
  computed: {
    previewView () {
      return !this.modes.editView
    },
    userInfo () {
      return this.$store.getters['session/userInfo']
    }
  },
  components: {
    LmForm,
    LmTextInput,
    LmButton,
    LmSelect,
    LmSuperPowerSelectV2,
    LmCheckbox,
    LmTextNumberEditorTextAreaPasswordBuilderUi,
    LmEmailInputBuilderUi,
    LmFormSubtitlesBuilderUi,
    LmFormEmptySpaceBuilderUi,
    LmFormSeparatorBuilderUi,
    LmSelectBuilderUi,
    LmRadioBuilderUi,
    LmSuperPowerSelectV2BuilderUi,
    LmButtonBuilderUi,
    LmDateBuilderUi,
    LmDateTimeBuilderUi,
    LmLeaFletMapBuilderUi,
    LmHtmlContentBuilderUi,
    LmFormPortalBuilderUi,
    LmColorPickerBuilderUi,
    draggable,
    LmFormBuilder,
    LmCommentBuilderUi,
    LmRepeatFieldsBuilderUi,
    LmCheckboxBuilderUi,
    LmUploadFilesBuilderUi,
    LmUploadImageBuilderUi,
    LmImportFromExcelBuilderUi,
    LmCustomBuilderUi,
    LmSignersBuilderUi,
    LmCodeInputBuilderUi,
    LmTreeSelectBuilderUi,
    LmEditorBuilderUi,
    LmTagsInputBuilderUi,
    LmPageBreakBuilderUi,
    LmDatePickerBuilderUi
  },
  methods: {
    leftValueToCompareOptions (formElementToExclude) {
      return {
        disabledOption: this.$t('Element'),
        content: this.form.formElements.filter(e => e.hasOwnProperty('formObjectToSendToServerProperty')).filter(e => e.id !== formElementToExclude.id).map(e => ({ value: e.formObjectToSendToServerProperty, text: e.componentProps.label }))
      }
    },
    shouldCreatePortal (componentName) {
      return componentName !== 'LmComment'
    },
    async setForm () {
      if (this.isOnEditMode) {
        await this.$refs.lmFormRef.activeEditMode({
          path: 'formbuilder',
          id: this.$route.params.id,
          paramsMode: true
        })
      } else {
        this.form = JSON.parse(this.initialForm)
        this.$refs.lmFormRef.showLoading()
        setTimeout(() => {
          this.$v.$reset()
          this.$refs.lmFormRef.hideLoading()
        }, 350)
      }
    },
    async submit () {
      await this.$refs.lmFormRef.validationForm(this.$v)
      try {
        this.isLoading = true
        const dataToSendToServer = { ...this.form }
        this.form.messageAfterPOSTorPUT = this.form.messageAfterPOSTorPUT || 'Guardada'
        this.form.pathToRedirectAfterPOSTorPUT = this.form.pathToRedirectAfterPOSTorPUT || '/'
        if (this.isOnEditMode) {
          const response = await commonsApi.axiosPutWrapper(`formbuilder/${this.$route.params.id}`, dataToSendToServer)
          this.currentID = response.data._id
          this.lmToastSuccessMessage('El formulario se guardó correctamente.', this.$t('success_saved'))
        } else {
          const response = await commonsApi.axiosPostWrapper('formbuilder/create', dataToSendToServer)
          this.currentID = response.data._id
          this.lmToastSuccessMessage('El formulario se guardó correctamente.', this.$t('success_saved'))
        }
        if (this.pathToRedirectOnSubmitOrCancel) {
          this.$router.push(this.pathToRedirectOnSubmitOrCancel)
        } else {
          this.$router.go(-1)
        }
      } catch (error) {
        this.lmToastDangerMessage(error.response.data.message, 'Error')
      } finally {
        this.isLoading = false
      }
    },
    openEditFormElement (index) {
      this.$root.$emit('bv::toggle::collapse', `collapse-${index}`)
    },
    title (component) {
      const componentLabel = component?.componentProps?.label
      const componentValue = component?.componentProps?.value
      const componentText = component?.componentProps?.text
      const { text } = this.componentsOptions.content.find(e => e.value === component?.componentName)
      let moreInfo = ''
      if (componentLabel) {
        moreInfo += ' - ' + componentLabel
      } else if (componentValue) {
        moreInfo += ' - ' + componentValue
      } else if (componentText) {
        moreInfo += ' - ' + componentText
      }
      return text + moreInfo
    },
    addFormElement (elementIndex) {
      this.selectorElementForm('LmTextInput', elementIndex)
    },
    inputHandler (event, index) {
      this.form.formElements[index] = event
    },
    validationsChangesHandler (payload) {
      let newValidation = {}
      const formValidationHasItem = Object.keys(this.form.validations).length
      if (formValidationHasItem) {
        for (const validation in this.form.validations) {
          const existFormObjectToSendToServerPropertyForValidation = this.form.formElements.find((el) => el.formObjectToSendToServerProperty === validation)
          if (existFormObjectToSendToServerPropertyForValidation) {
            newValidation = { ...newValidation, [validation]: this.form.validations[validation] }
          }
        }
        this.form.validations = { ...newValidation, ...payload }
      } else {
        this.form.validations = { ...this.form.validations, ...payload }
      }
    }
  },
  watch: {
    isOnEditMode () {
      this.setForm()
    },
    'form.pageTitleData' (val) {
      // Infiero el alias del formulario del título
      if (val) {
        const name = val.trim().toLowerCase().split(' ').join('_').replace(/\W+/g, '')
        this.form.alias = name
      }
    }
  },
  created () {
    this.initialForm = JSON.stringify(this.form)
  },
  async mounted () {
    this.setForm()

    // Set First element by default
    if (!this.isOnEditMode) {
      this.selectorElementForm('LmTextInput', 0)
    }
  },
  beforeDestroy () {
    this.$bvToast.hide('lm-toast-success-internal-link')
  }
}
</script>

<style scoped>
#form-builder-ui-form {
  display: flex;
  flex-wrap: wrap;
  margin-right: -15px;
  margin-left: -15px;
}

.circle {
  background: white;
  color: black;
  border-radius: 50%;
  width: 25px;
  height: 25px;
  text-align: center;
}

#lm-form-builder-ui .handle:hover {
  cursor: grab;
}

#LmFormBuilder {
  padding: 0;
}
</style>
